import React from 'react';
import { LinkTarget } from '@/declarations/models/LinkExternal';
import { BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import { LinkStyle } from '@/declarations/models/LinkStyle';
import { isDamsMediaItem } from '@/utils/typeChecks';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { BlockPreviewComponent } from '../../lib/declarations/EditorComponentTypes';
import LinksPreview from './LinksPreview';
import ImagePreview from '../../../components/ImagePreview';

export const BannerBlockPreview: BlockPreviewComponent = () => {
  const { block, blockIndex } = useCurrentBlock();
  const currentBlock = block as BannerBlock;

  const textClasses = `${currentBlock?.layout === 'img' || !currentBlock?.layout ? currentBlock?.textlineColor : ''} ${
    (currentBlock?.layout === 'img' || !currentBlock?.layout) &&
    currentBlock?.useTextbackgound &&
    !currentBlock?.useModuleContentBackgound
      ? 'text-background-lines'
      : ''
  }`;
  const hasTextbackground = `${
    currentBlock?.layout === 'img' && (currentBlock?.useModuleContentBackgound || currentBlock?.useTextbackgound)
      ? 'has-textbackground'
      : 'no-textbackground'
  }`;
  const hasMediaClass = `${
    currentBlock?.mediaobject?.id || isDamsMediaItem(currentBlock?.mediaobject) ? 'has-media' : 'no-media'
  } `;
  const mediaID =
    currentBlock?.mediaobject?.id ||
    (isDamsMediaItem(currentBlock?.mediaobject) ? currentBlock?.mediaobject?.dmsf_id : null);
  const transparentClass = `${currentBlock?.isTransparent ? 'transparent' : ''} `;
  const moduleBodyClass = `module__body ${textClasses}`;
  const titleClass = `module__heading ${textClasses} t-${currentBlock?.titleTransform} h-${currentBlock?.titleSize} f-${currentBlock?.titleWeight}`;
  const contentLayoutClass = `content__layout ${
    (currentBlock?.layout === 'img' || !currentBlock?.layout) && currentBlock?.useModuleContentBackgound
      ? 'text-background'
      : ''
  }`;

  const isLargeBanner = currentBlock?.size === 'xl' || currentBlock?.size === 'l' || currentBlock?.size === 'l-m';

  const convertToPercent = (v: number, max: number) => {
    return Number.parseFloat(((v as number) / (max || 100)).toFixed(2));
  };

  const alphaStrength = convertToPercent((currentBlock?.mediaobject?.alpha__strength as unknown as number) || 0, 100);
  const vignetteSize = currentBlock.mediaobject?.vignette__size as unknown as number;
  const vignetteStrength = convertToPercent(
    (currentBlock.mediaobject?.vignette__strength as unknown as number) || 0,
    100,
  );
  const gradientSize = currentBlock.mediaobject?.gradient__size as unknown as number;
  const gradientStrength = convertToPercent(
    (currentBlock.mediaobject?.gradient__strength as unknown as number) || 0,
    100,
  );

  let target = '_self';
  if (currentBlock?.items[0]?.type === 'link') {
    if (currentBlock?.items[0]?.target === LinkTarget.STANDARD) {
      target = '_blank';
    } else {
      target = currentBlock?.items[0]?.target || '_blank';
    }
  }

  let alphaColor = '0,0,0';
  let filterColor = '0,0,0';
  if (currentBlock?.mediaobject?.skin_on && currentBlock?.mediaobject?.skin?.rgb) {
    alphaColor = currentBlock.mediaobject.skin.rgb.replace('rgb(', '').replace(')', '').replace(';', '');
    filterColor = currentBlock.mediaobject.skin.rgb.replace('rgb(', '').replace(')', '').replace(';', '');
  }

  if (currentBlock?.mediaobject?.vignette_on || currentBlock?.mediaobject?.gradient_on) {
    alphaColor = '0,0,0';
  }

  return (
    <div className='blocktype--frontpage__module'>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <div
        className={`linkdiv ${currentBlock?.linkstyle === LinkStyle.CLICKABLE_MODULE ? 'module--link' : ''} `}
        data-target={target || '_self'}>
        <div
          className={`module module--banner module--${currentBlock?.size} ${currentBlock?.cssClass} layout--${
            currentBlock?.layout || 'img'
          } align--${currentBlock?.textAlign || 'default'} valign--${currentBlock?.textValign || 'default'} ${
            currentBlock?.skin || 'skin'
          } skin-banner ${hasMediaClass}  ${currentBlock?.mediaobject?.skin ? 'has-filter-skin' : ''}  module--${
            currentBlock?.size || ''
          } ${currentBlock?.linkstyle === LinkStyle.CLICKABLE_MODULE ? 'module--link' : ''} ${hasTextbackground}`}>
          <div
            className={`module__grid ${currentBlock?.skin || 'no-skin'} skin-banner ${
              currentBlock?.mediaobject?.id ? 'has-media' : 'no-media'
            }  ${transparentClass}`}>
            {currentBlock?.mediaobject?.alpha_on && (
              <style>{`#block_${blockIndex}_media_${mediaID}:after {
                  background-color: rgba(${alphaColor},${alphaStrength});}`}</style>
            )}

            {currentBlock?.mediaobject?.vignette_on && vignetteSize < 0 && (
              <style>{`#block_${blockIndex}_media_${mediaID}:after {
            background-image: -webkit-radial-gradient(center, ellipse cover, rgba(0,0,0, ${vignetteStrength}) 0%, rgba(0,0,0, ${
                (vignetteStrength as unknown as number) / 2
              }) ${100 + vignetteSize}%, rgba(${filterColor}, 0) 100%);}`}</style>
            )}

            {currentBlock?.mediaobject?.vignette_on && vignetteSize > 0 && (
              <style>{`#block_${blockIndex}_media_${mediaID}:after {
            background-image: -webkit-radial-gradient(center, ellipse cover, rgba(0,0,0,0) 0%, rgba(0,0,0, ${
              (vignetteStrength as unknown as number) / 2
            }) ${100 - vignetteSize}%, rgba(${filterColor}, ${vignetteStrength}) 100%);}`}</style>
            )}

            {currentBlock?.mediaobject?.gradient_on && gradientSize < 0 && (
              <style>
                {`#block_${blockIndex}_media_${mediaID}:after { background-image: -webkit-linear-gradient(top, rgba(0,0,0, 0) 0%, rgba(0,0,0, ${
                  (gradientStrength as unknown as number) / 2
                }) ${100 + gradientSize}%, rgba(${filterColor}, ${gradientStrength}) 100%);
              background-image: linear-gradient(to top, rgba(${filterColor}, 0) 0%, rgba(${filterColor}, ${
                  (gradientStrength as unknown as number) / 2
                }) ${100 + gradientSize}%, rgba(${filterColor}, ${gradientStrength}) 100%);`}
              </style>
            )}

            {currentBlock?.mediaobject?.gradient_on && gradientSize > 0 && (
              <style>{`#block_${blockIndex}_media_${mediaID}:after { background-image: -webkit-linear-gradient(top, rgba(${filterColor}, 0) 0%, rgba(${filterColor}, ${
                (gradientStrength as unknown as number) / 2
              }) ${100 - gradientSize}%, rgba(${filterColor}, ${gradientStrength}) 100%);`}</style>
            )}

            {currentBlock?.mediaobject?.id && currentBlock?.mediaobject?.type !== 'video' && (
              <ImagePreview
                dimension={isLargeBanner ? '1440x1440' : ''}
                effect={currentBlock?.effect}
                mediaObject={currentBlock?.mediaobject}
                id={`block_${blockIndex}_media_${mediaID}`}
              />
            )}

            {currentBlock?.mediaobject?.id &&
              currentBlock?.mediaobject?.type === 'video' &&
              currentBlock?.usePoster && (
                <ImagePreview
                  dimension={isLargeBanner ? '1440x1440' : ''}
                  mediaObject={currentBlock?.mediaobject?.content?.poster || currentBlock?.mediaobject}
                  id={`block_${blockIndex}_media_${mediaID}`}
                />
              )}

            {currentBlock?.mediaobject?.id &&
              currentBlock?.mediaobject?.type === 'video' &&
              !currentBlock?.usePoster && (
                <ImagePreview
                  opacity='0.4'
                  mediaObject={currentBlock?.mediaobject}
                  id={`block_${blockIndex}_media_${mediaID}`}
                />
              )}

            {isDamsMediaItem(currentBlock?.mediaobject) && (
              <ImagePreview
                dimension={isLargeBanner ? '1440x1440' : ''}
                effect={currentBlock?.effect}
                mediaObject={currentBlock?.mediaobject}
                id={`block_${blockIndex}_media_${mediaID}`}
              />
            )}

            {!currentBlock?.mediaobject?.id && !isDamsMediaItem(currentBlock?.mediaobject) && (
              <div className='module__media' />
            )}

            {currentBlock?.mediaCredits && (
              <div className='banner__overlaycontainer'>
                <div className='banner__credits'>{currentBlock?.mediaCredits}</div>
              </div>
            )}
            <div className='module__content'>
              <div className={contentLayoutClass}>
                <header className='module__head'>
                  {currentBlock?.icon && <i className={`module__icon i-icon-${currentBlock?.icon}`} />}
                  {currentBlock?.insertTitle && (
                    <div className={`banner__insert ${textClasses}`}>{currentBlock?.insertTitle}</div>
                  )}
                  {!currentBlock?.hidetitle && currentBlock?.title && (
                    <span className='banner__title'>
                      <span className='modify--size'>
                        <h2 className={titleClass}>{currentBlock?.title}</h2>
                      </span>
                    </span>
                  )}
                  {currentBlock.svgLogo && (
                    // eslint-disable-next-line react/no-danger
                    <div className='module__svglogo' dangerouslySetInnerHTML={{ __html: currentBlock.svgLogo }} />
                  )}
                </header>
                <div className={moduleBodyClass}>
                  <div
                    dangerouslySetInnerHTML={{ __html: currentBlock?.bodytext || '' }} // eslint-disable-line react/no-danger
                  />
                  {currentBlock?.audio?.id && (
                    /* eslint-disable-next-line jsx-a11y/media-has-caption */
                    <audio controls>
                      <source src={currentBlock?.audio?.play_urls?.mp3} type='audio/mp3' />
                    </audio>
                  )}
                </div>
                <footer className='module__foot'>
                  {currentBlock?.linkstyle !== LinkStyle.CLICKABLE_MODULE && (
                    <LinksPreview
                      linkItems={currentBlock?.items}
                      linkStyle={currentBlock?.linkstyle}
                      overrideListClass='module__links'
                    />
                  )}
                </footer>
                {currentBlock.nextButton && (
                  <ul className='module__buttons module__buttons--next'>
                    <li>
                      <button
                        aria-label='next'
                        type='button'
                        lang='no'
                        title='Neste skjermbilde'
                        className='nextsection nextbutton'>
                        <span />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerBlockPreview;
